import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaUsers,
  FaUser,
  FaCrown,
  FaArrowLeft,
  FaUserPlus,
  FaShareAlt,
  FaCalendarAlt,
  FaGift,
  FaTrophy,
  FaBan,
  FaUndo,
  FaUserSlash,
  FaFutbol,
} from "react-icons/fa";
import {
  Button,
  Message,
  Spin,
  Tag,
  Card,
  Table,
  Modal,
  Input,
  TextArea,
} from "@/frontend/components/common";
import { Tabs as AntTabs } from "antd";
import {
  bettingPoolService,
  poolParticipantService,
  poolInvitationService,
} from "@/frontend/services";
import type { PoolParticipantWithUserDTO } from "@/frontend/services/poolParticipantService";
import { useAuthStore } from "@/frontend/stores";
import type {
  BettingPoolWithTournamentResponseDTO,
  PoolLeaderboardEntryDTO,
} from "@/shared/dto";
import type { ColumnsType } from "antd/es/table";
import {
  AddParticipantsModal,
  type InviteData,
  PoolSettingsCard,
  PoolLeaderboardTab,
} from "./components";
import { BettingPoolForm } from "./BettingPoolForm";

interface ParticipantDisplay extends PoolParticipantWithUserDTO {
  displayName: string;
  isAdmin: boolean;
  isCreator: boolean;
}

export function BettingPoolDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();

  const [loading, setLoading] = useState(true);
  const [pool, setPool] = useState<BettingPoolWithTournamentResponseDTO | null>(null);
  const [participants, setParticipants] = useState<
    PoolParticipantWithUserDTO[]
  >([]);
  const [invitedUsers, setInvitedUsers] = useState<
    PoolParticipantWithUserDTO[]
  >([]);
  const [bannedUsers, setBannedUsers] = useState<PoolParticipantWithUserDTO[]>(
    [],
  );
  const [kickedUsers, setKickedUsers] = useState<PoolParticipantWithUserDTO[]>(
    [],
  );
  const [leaderboard, setLeaderboard] = useState<PoolLeaderboardEntryDTO[]>([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showBanModal, setShowBanModal] = useState(false);
  const [userToBan, setUserToBan] = useState<ParticipantDisplay | null>(null);
  const [banReason, setBanReason] = useState("");
  const [showKickModal, setShowKickModal] = useState(false);
  const [userToKick, setUserToKick] = useState<ParticipantDisplay | null>(null);
  const [kickReason, setKickReason] = useState("");

  const loadPoolData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [
        poolResponse,
        participantsResponse,
        invitedResponse,
        bannedResponse,
        kickedResponse,
      ] = await Promise.all([
        bettingPoolService.getByIdWithTournament(id),
        poolParticipantService.listByPool(id, "active"),
        poolParticipantService.listByPool(id, "invited"),
        poolParticipantService.listByPool(id, "banned"),
        poolParticipantService.listByPool(id, "kicked"),
      ]);

      if (poolResponse.success && poolResponse.data) {
        setPool(poolResponse.data);
      } else {
        Message.error({
          content: poolResponse.error || "Error al cargar el grupo",
        });
        navigate("/sports-predictions/pools/joined");
        return;
      }

      if (participantsResponse.success && participantsResponse.data) {
        setParticipants(participantsResponse.data);
      }

      if (invitedResponse.success && invitedResponse.data) {
        setInvitedUsers(invitedResponse.data);
      }

      if (bannedResponse.success && bannedResponse.data) {
        setBannedUsers(bannedResponse.data);
      }

      if (kickedResponse.success && kickedResponse.data) {
        setKickedUsers(kickedResponse.data);
      }

      try {
        setLeaderboardLoading(true);
        const lbResponse = await bettingPoolService.getLeaderboard(id);
        if (lbResponse.success && lbResponse.data) {
          setLeaderboard(lbResponse.data);
        }
      } catch (lbErr) {
        console.error("Error al cargar clasificación:", lbErr);
      } finally {
        setLeaderboardLoading(false);
      }
    } catch (error) {
      Message.error({
        content:
          error instanceof Error ? error.message : "Error al cargar datos",
      });
      navigate("/sports-predictions/pools/joined");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadPoolData();
  }, [loadPoolData]);

  const isUserAdmin = useMemo((): boolean => {
    if (!pool || !currentUser?.id) return false;

    // El creador siempre es admin
    if (pool.creatorId === currentUser.id) return true;

    // Verificar si es participante con rol admin
    const userParticipant = participants.find(
      (p) => p.userId === currentUser.id,
    );
    return userParticipant?.role === "admin";
  }, [pool, currentUser, participants]);

  const isUserParticipant = useMemo((): boolean => {
    if (!currentUser?.id) return false;
    return participants.some((p) => p.userId === currentUser.id);
  }, [currentUser, participants]);

  const participantDisplayList = useMemo((): ParticipantDisplay[] => {
    if (!pool) return [];

    return participants.map((p) => ({
      ...p,
      displayName: p.user
        ? `${p.user.firstName} ${p.user.lastName}`
        : `Usuario ${p.userId.slice(0, 8)}`,
      isAdmin: p.role === "admin" || p.userId === pool.creatorId,
      isCreator: p.userId === pool.creatorId,
    }));
  }, [participants, pool]);

  const existingParticipantIds = useMemo(
    () => participants.map((p) => p.userId),
    [participants],
  );

  const handleCopyInviteCode = () => {
    if (pool?.inviteCode) {
      navigator.clipboard.writeText(pool.inviteCode);
      Message.success({ content: "Código de invitación copiado" });
    }
  };

  const handleInviteUsers = async (data: InviteData) => {
    if (!pool?.id) {
      Message.error({ content: "No se pudo identificar el grupo" });
      return;
    }

    if (!currentUser?.id) {
      Message.error({ content: "Usuario no autenticado" });
      return;
    }

    const { registeredUsers, externalEmails } = data;

    const usersToInvite = registeredUsers.filter(
      (user) => !existingParticipantIds.includes(user.id),
    );

    if (usersToInvite.length === 0 && externalEmails.length === 0) {
      Message.warning({
        content:
          "Todos los usuarios seleccionados ya son participantes o no hay emails para invitar",
      });
      return;
    }

    try {
      let totalSuccess = 0;
      let totalFailed = 0;

      // Enviar invitaciones a usuarios registrados (usando sus emails)
      if (usersToInvite.length > 0) {
        const registeredResults = await Promise.allSettled(
          usersToInvite.map((user) =>
            poolInvitationService.send({
              email: user.email,
              poolId: pool.id,
            }),
          ),
        );

        const registeredSuccess = registeredResults.filter(
          (result) => result.status === "fulfilled" && result.value.success,
        ).length;
        const registeredFailed = usersToInvite.length - registeredSuccess;

        totalSuccess += registeredSuccess;
        totalFailed += registeredFailed;
      }

      // Enviar invitaciones a emails externos
      if (externalEmails.length > 0) {
        const externalResults = await Promise.allSettled(
          externalEmails.map((email) =>
            poolInvitationService.send({
              email,
              poolId: pool.id,
            }),
          ),
        );

        const externalSuccess = externalResults.filter(
          (result) => result.status === "fulfilled" && result.value.success,
        ).length;
        const externalFailed = externalEmails.length - externalSuccess;

        totalSuccess += externalSuccess;
        totalFailed += externalFailed;
      }

      if (totalSuccess > 0) {
        Message.success({
          content: `Invitaciones enviadas exitosamente a ${totalSuccess} destinatarios`,
        });
      }

      if (totalFailed > 0) {
        Message.warning({
          content: `${totalFailed} invitación${totalFailed > 1 ? "es" : ""} no se pudo enviar`,
        });
      }

      if (totalFailed > 0) {
        Message.warning({
          content: `${totalFailed} invitación${totalFailed > 1 ? "es" : ""} no se pudo enviar`,
        });
      }

      setShowAddModal(false);
      await loadPoolData();
    } catch (error) {
      Message.error({
        content:
          error instanceof Error
            ? error.message
            : "Error enviando invitaciones",
      });
    }
  };

  const handleResendInvitation = async (
    invitationId: string,
    email: string,
  ) => {
    try {
      const response = await poolInvitationService.resend(invitationId);

      if (response.success) {
        Message.success({
          content: `Invitación reenviada exitosamente a ${email}`,
        });
        await loadPoolData();
      } else {
        Message.error({
          content: response.error || "Error al reenviar invitación",
        });
      }
    } catch (error) {
      Message.error({
        content:
          error instanceof Error
            ? error.message
            : "Error al reenviar invitación",
      });
    }
  };

  const handleCancelInvitation = async (
    invitationId: string,
    email: string,
  ) => {
    try {
      const response = await poolInvitationService.cancel(invitationId);

      if (response.success) {
        Message.success({
          content: `Invitación cancelada para ${email}`,
        });
        await loadPoolData();
      } else {
        Message.error({
          content: response.error || "Error al cancelar invitación",
        });
      }
    } catch (error) {
      Message.error({
        content:
          error instanceof Error
            ? error.message
            : "Error al cancelar invitación",
      });
    }
  };

  const handleBanUser = (participant: ParticipantDisplay) => {
    setUserToBan(participant);
    setBanReason("");
    setShowBanModal(true);
  };

  const confirmBanUser = async () => {
    if (!userToBan || !pool) return;

    try {
      const response = await poolParticipantService.ban(
        pool.id,
        userToBan.userId,
        banReason || undefined,
      );

      if (response.success) {
        Message.success({
          content: `${userToBan.displayName} ha sido baneado del grupo`,
        });
        setShowBanModal(false);
        setUserToBan(null);
        setBanReason("");
        await loadPoolData();
      } else {
        Message.error({
          content: response.error || "Error al banear usuario",
        });
      }
    } catch (error) {
      Message.error({
        content:
          error instanceof Error ? error.message : "Error al banear usuario",
      });
    }
  };

  const handleUnbanUser = async (userId: string, userName: string) => {
    if (!pool) return;

    try {
      const response = await poolParticipantService.unban(pool.id, userId);

      if (response.success) {
        Message.success({
          content: `${userName} ha sido desbaneado del grupo`,
        });
        await loadPoolData();
      } else {
        Message.error({
          content: response.error || "Error al desbanear usuario",
        });
      }
    } catch (error) {
      Message.error({
        content:
          error instanceof Error ? error.message : "Error al desbanear usuario",
      });
    }
  };

  const handleKickUser = (participant: ParticipantDisplay) => {
    setUserToKick(participant);
    setKickReason("");
    setShowKickModal(true);
  };

  const confirmKickUser = async () => {
    if (!userToKick || !pool) return;

    try {
      const response = await poolParticipantService.kick(
        pool.id,
        userToKick.userId,
        kickReason || undefined,
      );

      if (response.success) {
        Message.success({
          content: `${userToKick.displayName} ha sido expulsado del grupo`,
        });
        setShowKickModal(false);
        setUserToKick(null);
        setKickReason("");
        await loadPoolData();
      } else {
        Message.error({
          content: response.error || "Error al expulsar usuario",
        });
      }
    } catch (error) {
      Message.error({
        content:
          error instanceof Error ? error.message : "Error al expulsar usuario",
      });
    }
  };

  const handleUnkickUser = async (userId: string, userName: string) => {
    if (!pool) return;

    try {
      const response = await poolParticipantService.unkick(pool.id, userId);

      if (response.success) {
        Message.success({
          content: `${userName} ha sido readmitido al grupo`,
        });
        await loadPoolData();
      } else {
        Message.error({
          content: response.error || "Error al readmitir usuario",
        });
      }
    } catch (error) {
      Message.error({
        content:
          error instanceof Error ? error.message : "Error al readmitir usuario",
      });
    }
  };

  const leaderboardColumns: ColumnsType<PoolLeaderboardEntryDTO> = [
    {
      title: "Posición",
      dataIndex: "rank",
      key: "rank",
      width: 120,
      align: "center",
      render: (rank: number) => {
        if (rank === 1) {
          return (
            <span className="flex items-center justify-center gap-1 font-bold text-yellow-600 bg-yellow-50 border border-yellow-200 px-2 py-1 rounded-full text-xs w-16 mx-auto">
              <FaTrophy className="text-yellow-500" /> 1º
            </span>
          );
        }
        if (rank === 2) {
          return (
            <span className="flex items-center justify-center gap-1 font-bold text-gray-500 bg-gray-50 border border-gray-200 px-2 py-1 rounded-full text-xs w-16 mx-auto">
              <FaTrophy className="text-gray-400" /> 2º
            </span>
          );
        }
        if (rank === 3) {
          return (
            <span className="flex items-center justify-center gap-1 font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full text-xs w-16 mx-auto">
              <FaTrophy className="text-amber-600" /> 3º
            </span>
          );
        }
        return (
          <span className="text-gray-500 font-medium">
            {rank}º
          </span>
        );
      },
    },
    {
      title: "Participante",
      key: "userName",
      render: (_, record) => {
        const isMe = record.userId === currentUser?.id;
        return (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center font-bold text-blue-600 text-sm">
              {record.userName.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <span className={`font-medium ${isMe ? "text-green-700 font-bold" : "text-gray-800"}`}>
                {record.userName} {isMe && <Tag color="green">Tú</Tag>}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Puntos Totales",
      dataIndex: "totalPoints",
      key: "totalPoints",
      width: 150,
      align: "right",
      render: (points: number) => (
        <span className="font-bold text-lg text-gray-900">{points} pts</span>
      ),
    },
  ];

  const participantColumns: ColumnsType<ParticipantDisplay> = [
    {
      title: "Participante",
      key: "participant",
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            {record.isCreator ? (
              <FaCrown className="text-yellow-500" />
            ) : (
              <FaUser className="text-blue-600" />
            )}
          </div>
          <div>
            <div className="font-medium flex items-center gap-2">
              {record.displayName}
              {record.isCreator && (
                <Tag color="gold" className="ml-1">
                  Creador
                </Tag>
              )}
              {record.isAdmin && !record.isCreator && (
                <Tag color="purple" className="ml-1">
                  Admin
                </Tag>
              )}
            </div>
            {record.user && (
              <div className="text-gray-500 text-sm">
                @{record.user.username}
                {record.user.userTag && (
                  <span className="ml-2">#{record.user.userTag}</span>
                )}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Rol",
      dataIndex: "role",
      key: "role",
      width: 120,
      render: (role: string) => (
        <Tag color={role === "admin" ? "purple" : "blue"}>
          {role === "admin" ? "Administrador" : "Jugador"}
        </Tag>
      ),
    },
    {
      title: "Estado",
      dataIndex: "statusId",
      key: "statusId",
      width: 120,
      render: (status: string) => {
        const colorMap: Record<string, string> = {
          active: "green",
          pending: "orange",
          rejected: "red",
          left: "default",
        };
        const labelMap: Record<string, string> = {
          active: "Activo",
          pending: "Pendiente",
          rejected: "Rechazado",
          left: "Salió",
        };
        return (
          <Tag color={colorMap[status] || "default"}>
            {labelMap[status] || status}
          </Tag>
        );
      },
    },
    {
      title: "Fecha de ingreso",
      dataIndex: "joinedAt",
      key: "joinedAt",
      width: 150,
      render: (date: Date | null) =>
        date ? (
          <span className="text-gray-600">
            {new Date(date).toLocaleDateString("es-ES")}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 200,
      render: (_, record) => {
        // Solo mostrar acciones si el usuario es admin
        if (!isUserAdmin) return null;

        // No permitir acciones al creador ni al usuario actual
        if (record.isCreator || record.userId === currentUser?.id) return null;

        return (
          <div className="flex gap-2">
            <Button
              size="small"
              danger
              icon={<FaBan />}
              onClick={() => handleBanUser(record)}
            >
              Banear
            </Button>
            <Button
              size="small"
              type="default"
              icon={<FaUserSlash />}
              onClick={() => handleKickUser(record)}
            >
              Expulsar
            </Button>
          </div>
        );
      },
    },
  ];

  const invitedColumns: ColumnsType<PoolParticipantWithUserDTO> = [
    {
      title: "Usuario / Email",
      key: "userOrEmail",
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
            <FaUser className="text-amber-600" />
          </div>
          <div>
            {record.user ? (
              <>
                <div className="font-medium">
                  {record.user.firstName} {record.user.lastName}
                </div>
                <div className="text-gray-500 text-sm">{record.user.email}</div>
              </>
            ) : (
              <div className="font-medium text-gray-700">
                {record.email}
                <Tag color="orange" className="ml-2">
                  Externo
                </Tag>
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Estado",
      key: "status",
      width: 120,
      render: () => <Tag color="orange">Invitado</Tag>,
    },
    {
      title: "Expira",
      dataIndex: "expiresAt",
      key: "expiresAt",
      width: 150,
      render: (date: string | null) =>
        date ? (
          <span className="text-gray-600">
            {new Date(date).toLocaleDateString("es-ES")}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 200,
      render: (_, record) => {
        if (!record.invitationId) {
          return null;
        }

        return isUserAdmin ? (
          <div className="flex gap-2">
            <Button
              size="small"
              onClick={() =>
                handleResendInvitation(
                  record.invitationId!,
                  record.email || record.user?.email || "",
                )
              }
            >
              Reenviar
            </Button>
            <Button
              size="small"
              danger
              onClick={() =>
                handleCancelInvitation(
                  record.invitationId!,
                  record.email || record.user?.email || "",
                )
              }
            >
              Cancelar
            </Button>
          </div>
        ) : null;
      },
    },
  ];

  const bannedColumns: ColumnsType<PoolParticipantWithUserDTO> = [
    {
      title: "Participante",
      key: "participant",
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <FaUser className="text-red-600" />
          </div>
          <div>
            {record.user ? (
              <>
                <div className="font-medium">
                  {record.user.firstName} {record.user.lastName}
                </div>
                <div className="text-gray-500 text-sm">
                  @{record.user.username}
                  {record.user.userTag && (
                    <span className="ml-2">#{record.user.userTag}</span>
                  )}
                </div>
              </>
            ) : (
              <div className="font-medium text-gray-700">{record.email}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Estado",
      key: "status",
      width: 120,
      render: () => <Tag color="red">Baneado</Tag>,
    },
    {
      title: "Razón",
      dataIndex: "exitReason",
      key: "exitReason",
      width: 250,
      render: (reason: string | null) =>
        reason ? (
          <span className="text-gray-600">{reason}</span>
        ) : (
          <span className="text-gray-400 italic">Sin razón especificada</span>
        ),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 150,
      render: (_, record) => {
        return isUserAdmin ? (
          <Button
            size="small"
            type="primary"
            icon={<FaUndo />}
            onClick={() =>
              handleUnbanUser(
                record.userId,
                record.user
                  ? `${record.user.firstName} ${record.user.lastName}`
                  : record.email || "",
              )
            }
          >
            Desbanear
          </Button>
        ) : null;
      },
    },
  ];

  const kickedColumns: ColumnsType<PoolParticipantWithUserDTO> = [
    {
      title: "Participante",
      key: "participant",
      render: (_, record) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
            <FaUser className="text-orange-600" />
          </div>
          <div>
            {record.user ? (
              <>
                <div className="font-medium">
                  {record.user.firstName} {record.user.lastName}
                </div>
                <div className="text-gray-500 text-sm">
                  @{record.user.username}
                  {record.user.userTag && (
                    <span className="ml-2">#{record.user.userTag}</span>
                  )}
                </div>
              </>
            ) : (
              <div className="font-medium text-gray-700">{record.email}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      title: "Estado",
      key: "status",
      width: 120,
      render: () => <Tag color="orange">Expulsado</Tag>,
    },
    {
      title: "Razón",
      dataIndex: "exitReason",
      key: "exitReason",
      width: 250,
      render: (reason: string | null) =>
        reason ? (
          <span className="text-gray-600">{reason}</span>
        ) : (
          <span className="text-gray-400 italic">Sin razón especificada</span>
        ),
    },
    {
      title: "Acciones",
      key: "actions",
      width: 150,
      render: (_, record) => {
        return isUserAdmin ? (
          <Button
            size="small"
            type="primary"
            icon={<FaUndo />}
            onClick={() =>
              handleUnkickUser(
                record.userId,
                record.user
                  ? `${record.user.firstName} ${record.user.lastName}`
                  : record.email || "",
              )
            }
          >
            Readmitir
          </Button>
        ) : null;
      },
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Spin size="large" />
      </div>
    );
  }

  if (!pool) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm text-center">
        <p className="text-gray-500">No se encontró el grupo de apuestas</p>
        <Button
          type="primary"
          className="mt-4"
          onClick={() => navigate("/sports-predictions/pools/my-pools")}
        >
          Volver a mis grupos
        </Button>
      </div>
    );
  }

  // Verificar que el usuario sea participante o admin
  if (!isUserParticipant && !isUserAdmin) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-sm text-center">
        <p className="text-gray-500">
          No tienes acceso a este grupo de apuestas
        </p>
        <Button
          type="primary"
          className="mt-4"
          onClick={() => navigate("/sports-predictions/pools/my-pools")}
        >
          Volver a mis grupos
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <AntTabs defaultActiveKey="leaderboard">
        {isUserAdmin && (
          <AntTabs.TabPane tab="Configurar" key="configurar">
            <div className="mt-2">
              <BettingPoolForm
                mode="edit"
                existingPool={pool}
                existingTournament={null}
                compact
              />
            </div>
          </AntTabs.TabPane>
        )}

        <AntTabs.TabPane tab="🏆 Clasificación" key="leaderboard">
          <div className="mt-2">
            <PoolLeaderboardTab
              leaderboard={leaderboard}
              loading={leaderboardLoading}
              currentUserId={currentUser?.id}
              pool={pool}
            />
          </div>
        </AntTabs.TabPane>

        <AntTabs.TabPane tab="Configuración" key="settings">
          {isUserAdmin ? (
            <div className="mt-4">
              <PoolSettingsCard
                pool={pool}
                isAdmin={isUserAdmin}
                onUpdate={loadPoolData}
              />
            </div>
          ) : (
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <p className="text-gray-600">
                No tienes permisos para ver la configuración
              </p>
            </div>
          )}
        </AntTabs.TabPane>

        <AntTabs.TabPane tab="Detalle" key="detail">
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex items-center gap-4 mb-4">
              <Button
                type="text"
                icon={<FaArrowLeft />}
                onClick={() => navigate(-1)}
              >
                Volver
              </Button>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-gray-800">
                    {pool.name}
                  </h1>
                  {pool.isPrivate ? (
                    <Tag color="orange">Privado</Tag>
                  ) : (
                    <Tag color="blue">Público</Tag>
                  )}
                </div>
                {pool.description && (
                  <p className="text-gray-600 mt-1">{pool.description}</p>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  type="primary"
                  icon={<FaFutbol />}
                  onClick={() => navigate(`/sports-predictions/pools/${pool.id}/predictions`)}
                >
                  Ver partidos
                </Button>
                <Button icon={<FaShareAlt />} onClick={handleCopyInviteCode}>
                  Compartir código
                </Button>
              </div>
            </div>

            {/* Información del grupo */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
              <Card className="bg-blue-50 border-blue-200">
                <div className="flex items-center gap-3">
                  <FaUsers className="text-blue-600 text-xl" />
                  <div>
                    <div className="text-sm text-gray-600">Participantes</div>
                    <div className="text-xl font-bold text-blue-600">
                      {participants.length}
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="bg-green-50 border-green-200 md:col-span-2">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <FaGift className="text-green-600" />
                    Premiación
                  </div>
                  {pool.prizes?.length ? (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {pool.prizes.map((prize) => (
                        <div
                          key={prize.position}
                          className="rounded-lg border border-green-200 bg-white/80 p-3"
                        >
                          <div className="text-xs font-semibold uppercase tracking-wide text-green-700">
                            {prize.position === 1
                              ? "1er puesto"
                              : prize.position === 2
                                ? "2do puesto"
                                : "3er puesto"}
                          </div>
                          <div className="mt-2 flex items-start gap-3">
                            {prize.prizeImageUrl ? (
                              <img
                                src={prize.prizeImageUrl}
                                alt={prize.prizeType?.name ?? "Premio"}
                                className="h-12 w-12 rounded-lg border border-green-200 object-cover"
                              />
                            ) : (
                              <FaGift className="text-green-600 text-lg mt-0.5" />
                            )}
                            <div className="min-w-0">
                              <div className="font-semibold text-green-700">
                                {prize.prizeType?.name ?? "Sin tipo"}
                              </div>
                              {prize.prizeValue && (
                                <div className="text-sm text-green-800 mt-1 break-words">
                                  {prize.prizeValue}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">Sin premiación configurada</div>
                  )}
                </div>
              </Card>

              <Card className="bg-purple-50 border-purple-200">
                <div className="flex items-center gap-3">
                  <FaCalendarAlt className="text-purple-600 text-xl" />
                  <div>
                    <div className="text-sm text-gray-600">
                      Código de invitación
                    </div>
                    <div className="text-lg font-bold text-purple-600 font-mono">
                      {pool.inviteCode}
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="bg-orange-50 border-orange-200">
                <div className="flex items-center gap-3">
                  <FaUser className="text-orange-600 text-xl" />
                  <div>
                    <div className="text-sm text-gray-600">Tu rol</div>
                    <div className="text-lg font-bold text-orange-600">
                      {isUserAdmin ? "Administrador" : "Participante"}
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </AntTabs.TabPane>

        <AntTabs.TabPane tab="Miembros" key="members">
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">
                Participantes ({participantDisplayList.length})
              </h2>
              {isUserAdmin && (
                <Button
                  type="primary"
                  icon={<FaUserPlus />}
                  onClick={() => setShowAddModal(true)}
                >
                  Agregar Participantes
                </Button>
              )}
            </div>

            {participantDisplayList.length > 0 ? (
              <Table
                rowKey={(record) => `${record.poolId}-${record.userId}`}
                dataSource={participantDisplayList}
                columns={participantColumns}
                pagination={false}
                locale={{ emptyText: "No hay participantes en este grupo" }}
              />
            ) : (
              <p className="text-gray-500 italic">
                No hay participantes en este grupo
              </p>
            )}
          </div>

          {isUserAdmin && invitedUsers.length > 0 && (
            <div className="bg-white rounded-lg p-6 shadow-sm mt-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">
                  Invitaciones Pendientes
                  <Tag color="orange" className="ml-2">
                    {invitedUsers.length}
                  </Tag>
                </h2>
              </div>

              <Table
                rowKey={(record) =>
                  `${record.poolId}-${record.userId || record.email}`
                }
                dataSource={invitedUsers}
                columns={invitedColumns}
                pagination={false}
                locale={{ emptyText: "No hay invitaciones pendientes" }}
              />
            </div>
          )}

          {isUserAdmin && bannedUsers.length > 0 && (
            <div className="bg-white rounded-lg p-6 shadow-sm mt-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">
                  Usuarios Baneados
                  <Tag color="red" className="ml-2">
                    {bannedUsers.length}
                  </Tag>
                </h2>
              </div>

              <Table
                rowKey={(record) => `${record.poolId}-${record.userId}`}
                dataSource={bannedUsers}
                columns={bannedColumns}
                pagination={false}
                locale={{ emptyText: "No hay usuarios baneados" }}
              />
            </div>
          )}

          {isUserAdmin && kickedUsers.length > 0 && (
            <div className="bg-white rounded-lg p-6 shadow-sm mt-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">
                  Usuarios Expulsados
                  <Tag color="orange" className="ml-2">
                    {kickedUsers.length}
                  </Tag>
                </h2>
              </div>

              <Table
                rowKey={(record) => `${record.poolId}-${record.userId}`}
                dataSource={kickedUsers}
                columns={kickedColumns}
                pagination={false}
                locale={{ emptyText: "No hay usuarios expulsados" }}
              />
            </div>
          )}
        </AntTabs.TabPane>
      </AntTabs>

      {/* Modal de agregar participantes */}
      <AddParticipantsModal
        visible={showAddModal}
        onClose={() => setShowAddModal(false)}
        onInvite={handleInviteUsers}
        poolId={pool.id}
        existingParticipantIds={existingParticipantIds}
      />

      {/* Modal de banear usuario */}
      <Modal
        title="Banear usuario"
        open={showBanModal}
        onOk={confirmBanUser}
        onCancel={() => {
          setShowBanModal(false);
          setUserToBan(null);
          setBanReason("");
        }}
        okText="Confirmar baneo"
        cancelText="Cancelar"
        okButtonProps={{ danger: true }}
      >
        {userToBan && (
          <div className="space-y-4">
            <p className="text-gray-700">
              ¿Estás seguro de que deseas banear a{" "}
              <strong>{userToBan.displayName}</strong> del grupo?
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Razón del baneo (opcional)
              </label>
              <TextArea
                value={banReason}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setBanReason(e.target.value)
                }
                placeholder="Describe el motivo del baneo..."
                rows={4}
                maxLength={500}
                showCount
              />
            </div>
            <p className="text-sm text-gray-500">
              El usuario no podrá acceder al grupo hasta que sea desbaneado.
            </p>
          </div>
        )}
      </Modal>

      {/* Modal de expulsar usuario */}
      <Modal
        title="Expulsar usuario"
        open={showKickModal}
        onOk={confirmKickUser}
        onCancel={() => {
          setShowKickModal(false);
          setUserToKick(null);
          setKickReason("");
        }}
        okText="Confirmar expulsión"
        cancelText="Cancelar"
        okButtonProps={{ danger: true }}
      >
        {userToKick && (
          <div className="space-y-4">
            <p className="text-gray-700">
              ¿Estás seguro de que deseas expulsar a{" "}
              <strong>{userToKick.displayName}</strong> del grupo?
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Razón de la expulsión (opcional)
              </label>
              <TextArea
                value={kickReason}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setKickReason(e.target.value)
                }
                placeholder="Describe el motivo de la expulsión..."
                rows={4}
                maxLength={500}
                showCount
              />
            </div>
            <p className="text-sm text-gray-500">
              El usuario será expulsado del grupo pero podrá ser readmitido
              posteriormente.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}
